import React from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';
import CountryFlag from 'react-country-flag';
import lookup from 'country-code-lookup';
import { CUSTOM_FLAGS } from './team-crest';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';

const ORANGE = '#ff9800';
const AMBER = '#ffc107';
const GREEN = '#43c463';
const RED = '#ef5350';
const LINE = '1px solid rgba(255,255,255,0.08)';

/* ----- helpers ----- */

/** Round flag, falls back to 2-letter initials box */
function FlagImg({ name, size = 20 }) {
  const cc = lookup.byCountry(name);
  const custom = name ? CUSTOM_FLAGS[name] : null;
  const style = { width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 };
  if (cc) return <CountryFlag countryCode={cc.iso2} svg style={style} />;
  if (custom) return <img src={custom} alt={name} style={style} />;
  return (
    <Box sx={{ ...style, bgcolor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: 7, fontWeight: 700, color: '#fff' }}>{(name || '').slice(0, 2).toUpperCase()}</Typography>
    </Box>
  );
}

/** 3-letter country code, e.g. "ENG", "BRA". Falls back to first 3 chars. */
function shortCode(name) {
  // Custom short codes for non-sovereign nations
  const CUSTOM_CODES = {
    'Scotland': 'SCO',
    'England': 'ENG',
    'Wales': 'WAL',
    'Northern Ireland': 'NIR',
    'Kosovo': 'XKX',
    'Palestine': 'PLE',
    'Catalonia': 'CAT',
    'Basque Country': 'EUS',
    'Ivory Coast': 'CIV',
  };
  if (CUSTOM_CODES[name]) return CUSTOM_CODES[name];
  return lookup.byCountry(name)?.iso3 || (name || '').slice(0, 3).toUpperCase();
}

function statusOf(event) {
  const evtTime = DateTime.fromFormat(event.time, "yyyy-MM-dd'T'HH:mm:ss'Z'", { zone: 'utc' }).toLocal();
  const isFuture = evtTime > DateTime.now();
  const hasResult = event.score1 !== null && event.score1 !== undefined && event.score1 >= 0
    && event.score2 !== null && event.score2 !== undefined && event.score2 >= 0;
  return { evtTime, status: isFuture ? 'upcoming' : hasResult ? 'finished' : 'live' };
}

const STATUS_COLOR = { upcoming: '#888', live: ORANGE, finished: '#fff' };

/** Compact bet badge shown below the teams row */
function BetBadge({ event, status }) {
  const bet = event.my_bet;
  const hasBet = bet && bet.score1 !== null && bet.score2 !== null;

  if (!hasBet) {
    if (status === 'upcoming') return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
        <WarningAmberIcon sx={{ fontSize: 11, color: RED }} />
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: RED }}>No bet</Typography>
      </Box>
    );
    return null;
  }

  const finished = status === 'finished';
  const win = finished && bet.points === 3;
  const pointsKnown = finished && bet.points !== null && bet.points !== undefined;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {!finished && <LockIcon sx={{ fontSize: 11, color: GREEN }} />}
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: win ? AMBER : finished ? '#ccc' : '#888' }}>
        {bet.score1}:{bet.score2}
      </Typography>
      {pointsKnown && (
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.625, py: '1px', borderRadius: 999,
          background: win ? AMBER : 'rgba(255,255,255,0.08)',
          border: win ? 'none' : '1px solid rgba(255,255,255,0.12)',
        }}>
          {win && <EmojiEventsIcon sx={{ fontSize: 10, color: '#1a1207' }} />}
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: win ? '#1a1207' : '#888' }}>
            {bet.points}pt{bet.points === 1 ? '' : 's'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function TimelineEvent({ event, onClick }) {
  const { evtTime, status } = statusOf(event);
  const hasResult = status === 'finished';
  const dotColor = status === 'live' ? ORANGE : status === 'finished' ? GREEN : '#444';

  return (
    <Box onClick={onClick} sx={{
      display: 'flex', gap: 0, cursor: 'pointer',
      '&:hover .tl-card': { borderColor: 'rgba(255,152,0,0.35)', background: '#1c1c1c' },
    }}>

      {/* ── left: time ── */}
      <Box sx={{ width: 44, flexShrink: 0, pt: '12px', textAlign: 'right', pr: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: STATUS_COLOR[status], lineHeight: 1 }}>
          {evtTime.toFormat('HH:mm')}
        </Typography>
      </Box>

      {/* ── centre: dot + vertical line ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 18 }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor,
          border: '2px solid #121212', mt: '14px', flexShrink: 0, zIndex: 1,
          boxShadow: status === 'live' ? `0 0 0 3px rgba(255,152,0,0.22)` : 'none',
        }} />
        <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(255,255,255,0.06)', mt: 0 }} />
      </Box>

      {/* ── right: card ── */}
      <Box className="tl-card" sx={{
        flex: 1, ml: 1.25, mb: 1, p: '8px 10px', borderRadius: '10px',
        background: '#161616', border: LINE,
        transition: 'border-color .15s ease, background .15s ease',
        minWidth: 0,
      }}>

        {/* teams + score row */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 0.75 }}>

          {/* team 1 — flag left, code right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
            <FlagImg name={event.team1} />
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', letterSpacing: 0.3, lineHeight: 1 }}>
              {shortCode(event.team1)}
            </Typography>
          </Box>

          {/* centre: score or status pill */}
          <Box sx={{ textAlign: 'center', px: 0.5 }}>
            {hasResult ? (
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: '"Archivo", sans-serif', letterSpacing: 1, lineHeight: 1 }}>
                {event.score1}:{event.score2}
              </Typography>
            ) : (
              <Box sx={{
                px: 0.75, py: '2px', borderRadius: 999,
                background: status === 'live' ? 'rgba(255,152,0,0.14)' : 'rgba(255,255,255,0.06)',
                border: status === 'live' ? '1px solid rgba(255,152,0,0.4)' : '1px solid rgba(255,255,255,0.10)',
              }}>
                <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: STATUS_COLOR[status], whiteSpace: 'nowrap' }}>
                  {status === 'live' ? 'Live' : 'VS'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* team 2 — code left, flag right */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0, justifyContent: 'flex-end' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#fff', letterSpacing: 0.3, lineHeight: 1 }}>
              {shortCode(event.team2)}
            </Typography>
            <FlagImg name={event.team2} />
          </Box>
        </Box>

        {/* bet badge row */}
        <Box sx={{ mt: 0.625, display: 'flex', justifyContent: 'center' }}>
          <BetBadge event={event} status={status} />
        </Box>
      </Box>
    </Box>
  );
}

/* ----- group events by local date ----- */
function groupByDay(events) {
  const groups = {};
  events.forEach(evt => {
    const evtTime = DateTime.fromFormat(evt.time, "yyyy-MM-dd'T'HH:mm:ss'Z'", { zone: 'utc' }).toLocal();
    const key = evtTime.toFormat('yyyy-MM-dd');
    if (!groups[key]) groups[key] = { label: evtTime, events: [] };
    groups[key].events.push(evt);
  });
  return Object.values(groups).sort((a, b) => a.label < b.label ? -1 : 1);
}

function DayHeader({ dt }) {
  const today = DateTime.now().startOf('day');
  const day = dt.startOf('day');
  const isToday = day.equals(today);
  const isTomorrow = day.equals(today.plus({ days: 1 }));
  const label = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : dt.toFormat('cccc, dd MMM');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25, mt: 1 }}>
      <Box sx={{ width: 44, flexShrink: 0 }} />
      <Box sx={{ width: 18, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: 2, height: 10, bgcolor: 'rgba(255,255,255,0.06)' }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: isToday ? ORANGE : '#666' }}>
          {label}
        </Typography>
        {isToday && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: ORANGE }} />}
      </Box>
    </Box>
  );
}

export default function EventTimeline({ events }) {
  const navigate = useNavigate();
  if (!events || events.length === 0) return (
    <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 4 }}>No events to display.</Typography>
  );

  const days = groupByDay(events);
  const navEvents = events.map((e) => ({ id: e.id, team1: e.team1, team2: e.team2 }));
  const openEvent = (id) => navigate(`/event/${id}`, { state: { navEvents } });

  return (
    <Box sx={{ position: 'relative' }}>
      {days.map((day) => (
        <Box key={day.label.toISO()}>
          <DayHeader dt={day.label} />
          {day.events.map(evt => (
            <TimelineEvent key={evt.id} event={evt} onClick={() => openEvent(evt.id)} />
          ))}
        </Box>
      ))}
    </Box>
  );
}
